import type { ReactNode } from 'react'
import type {
	DropdownMenuProps,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import React, { useMemo, useRef } from 'react'

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

import { parseFromValuesOrFunc } from '../../utils/utils'
import { SRT_ActionMenuItem } from './SRT_ActionMenuItem'

export type SRT_RowActionMenuProps<TData extends SRT_RowData> = {
	anchorEl: HTMLElement | null
	handleEdit: (event: React.MouseEvent) => void
	row: SRT_Row<TData>
	setAnchorEl: (anchorEl: HTMLElement | null) => void
	staticRowIndex?: number
	table: SRT_TableInstance<TData>
} & Partial<DropdownMenuProps>

export const SRT_RowActionMenu = <TData extends SRT_RowData>({
	anchorEl,
	handleEdit,
	row,
	setAnchorEl,
	staticRowIndex,
	table,
}: SRT_RowActionMenuProps<TData>) => {
	const triggerRef = useRef<HTMLDivElement>(null)

	const {
		options: {
			editDisplayMode,
			enableEditing,
			icons: { EditIcon },
			localization,
			renderRowActionMenuItems,
		},
	} = table

	const menuItems = useMemo(() => {
		const items: ReactNode[] = []
		const editItem = parseFromValuesOrFunc(enableEditing, row) &&
			['modal', 'row'].includes(editDisplayMode ?? '') && (
				<SRT_ActionMenuItem
					key='edit'
					icon={<EditIcon />}
					label={localization.edit}
					onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
						e.stopPropagation()
						if ('button' in e) {
							handleEdit(e as React.MouseEvent)
						}
					}}
					table={table}
				/>
			)
		if (editItem) items.push(editItem)
		const rowActionMenuItems = renderRowActionMenuItems?.({
			closeMenu: () => setAnchorEl(null),
			row,
			staticRowIndex,
			table,
		})
		if (rowActionMenuItems?.length) items.push(...rowActionMenuItems)

		return items
	}, [renderRowActionMenuItems, row, staticRowIndex, table])

	if (!menuItems.length) return null

	const handleTriggerClick = () => {
		if (anchorEl) {
			setAnchorEl(triggerRef.current)
		}
	}

	const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleTriggerClick()
		}
	}

	return (
		<DropdownMenu open={!!anchorEl} onOpenChange={() => setAnchorEl(null)}>
			<DropdownMenuTrigger asChild>
				<div
					ref={triggerRef}
					onClick={handleTriggerClick}
					onKeyDown={handleTriggerKeyDown}
					role='button'
					tabIndex={0}
				/>
			</DropdownMenuTrigger>
			<DropdownMenuContent onClick={(event) => event.stopPropagation()}>
				{menuItems}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
